import React, { useState } from 'react';
import {
  Play,
  List,
  Check,
  X,
  Star,
  Users,
  MapPin,
  Clock,
  ChevronDown,
} from 'lucide-react';
import { Linkify } from '../utils/audioUtils';

const ArchiveListItem = ({
  item,
  onPlay,
  onQueue,
  onRemove,
  isInQueue,
  saved,
  total,
  toggleFavorite,
  isFavorite,
  onStartParty,
}) => {
  const [expanded, setExpanded] = useState(false);
  const progressPct = total > 0 ? (saved / total) * 100 : 0;

  return (
    <div className="group p-4 hover:bg-slate-800/50 transition-colors flex flex-col md:flex-row gap-4 items-start md:items-start border-b border-slate-800 last:border-0">
      <div className="relative w-full md:w-40 aspect-video bg-slate-800 rounded-lg overflow-hidden flex-shrink-0">
        <picture>
          <source
            media="(max-width: 767px)"
            srcSet={item.thumbnail?.replace('.png', '_s.png')}
          />
          <source
            media="(min-width: 768px)"
            srcSet={item.thumbnail?.replace('.png', '_m.png')}
          />
          <img
            src={item.thumbnail?.replace('.png', '_m.png')}
            loading="lazy"
            className="w-full h-full object-cover opacity-80"
            alt={item.title}
          />
        </picture>

        <div
          className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/0 transition-colors cursor-pointer"
          onClick={() => onPlay(item)}
        >
          <Play
            size={32}
            className="text-white opacity-0 group-hover:opacity-100 drop-shadow-md"
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
          <div
            className="h-full bg-red-500"
            style={{ width: `${Math.min(progressPct, 100)}%` }}
          />
        </div>
        <div className="absolute bottom-1 right-1 bg-black/80 px-1.5 py-0.5 text-[10px] text-white rounded font-mono">
          {item.duration}
        </div>
      </div>

      <div className="flex-grow min-w-0 w-full">
        <div className="flex justify-between items-start gap-4">
          <h3
            className="text-slate-200 font-medium text-base leading-tight mb-1 cursor-pointer hover:text-blue-400"
            onClick={() => onPlay(item)}
          >
            {item.number}) {item.title}
          </h3>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mb-2">
          <span className="flex items-center gap-1">
            <MapPin size={12} /> {item.place}
          </span>
          <span className="flex items-center gap-1">
            <Clock size={12} /> {item.date}
          </span>
        </div>

        {item.description && (
          <div className="text-sm text-slate-400 mb-3">
            <div
              className={`relative ${
                expanded ? '' : 'max-h-[4.5em] overflow-y-auto'
              }`}
            >
              <div className="whitespace-pre-wrap leading-relaxed">
                <Linkify text={item.description} />
              </div>
            </div>
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs font-medium text-blue-400 hover:text-blue-300 mt-1 flex items-center gap-1"
            >
              {expanded ? 'Show Less' : 'Show More'}{' '}
              <ChevronDown
                size={12}
                className={`transition-transform ${
                  expanded ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 w-full md:w-auto mt-auto">
        <button
          onClick={() => onPlay(item)}
          className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-blue-600 text-slate-300 hover:text-white rounded-lg border border-slate-700 hover:border-blue-500 transition-all text-sm font-medium group/btn"
        >
          <Play size={16} /> <span className="md:hidden lg:inline">Play</span>
        </button>
        <button
          onClick={() => (isInQueue ? onRemove(item.id) : onQueue(item))}
          className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium group/btn ${
            isInQueue
              ? 'bg-blue-600 text-white border-blue-500 hover:bg-red-600 hover:border-red-500'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border-slate-700'
          }`}
        >
          {isInQueue ? (
            <Check size={16} className="group-hover/btn:hidden" />
          ) : (
            <List size={16} />
          )}
          {isInQueue && (
            <X size={16} className="hidden group-hover/btn:block" />
          )}
          <span className="md:hidden lg:inline">
            {isInQueue ? (
              <span className="group-hover/btn:hidden">Queued</span>
            ) : (
              'Queue'
            )}
            {isInQueue && (
              <span className="hidden group-hover/btn:inline">Remove</span>
            )}
          </span>
        </button>

        <button
          onClick={() => toggleFavorite(item.id)}
          className={`p-2 rounded-lg border border-slate-700 hover:bg-slate-800 ${
            isFavorite ? 'text-yellow-400' : 'text-slate-400'
          }`}
        >
          <Star size={16} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>

        <button
          onClick={() => onStartParty(item)}
          className="p-2 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-400 hover:text-green-400"
        >
          <Users size={16} />
        </button>
      </div>
    </div>
  );
};

export default ArchiveListItem;
